import { useState, useEffect } from "react";
// import { notifyError } from "../../../../../assets/toastService";
import { Product } from "src/shared/services/interfacev2";
import { getProduct } from "src/shared/services/servicesv2";

export const useProducts = (showDeleted: boolean, page: number, rowsPerPage: number, search: string, categoryFilter: string) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // ✅ Helper function to safely map products
  const mapProducts = (data: any[]) => {
    if (!Array.isArray(data)) {
      console.warn("Products data is not an array:", data);
      return [];
    }
    return data.map((product: any) => ({
      ...product,
      staff_list: Array.isArray(product.staff_list)
        ? product.staff_list.map((staff: any) => ({
            name: typeof staff === "string" ? staff : staff.name || staff.staff_name || "",
          }))
        : [],
    }));
  };

  const fetchProducts = async () => {
    setLoading(true);
    setProducts([]);

    try {
      const response = await getProduct(showDeleted, page + 1, rowsPerPage, debouncedSearch.trim() || undefined, categoryFilter !== "all" ? categoryFilter : undefined, "displayOrder", "asc");

      // ✅ Safe check for response structure
      const productsData = response?.data?.results || [];
      const total = response?.data?.totalRecords || 0;

      // ✅ Map products safely
      const mappedProducts = mapProducts(productsData);

      setProducts(mappedProducts);
      setTotalRecords(total);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
      setTotalRecords(0);
      // notifyError("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const fetchProductsQuietly = async () => {
    try {
      const response = await getProduct(showDeleted, page + 1, rowsPerPage, debouncedSearch.trim() || undefined, categoryFilter !== "all" ? categoryFilter : undefined, "displayOrder", "asc");

      // ✅ Safe check for response structure
      const productsData = response?.data?.results || [];
      const mappedProducts = mapProducts(productsData);

      setProducts(mappedProducts);
      setTotalRecords(response?.data?.totalRecords || 0);
    } catch (error) {
      console.error("Error quietly fetching products:", error);
      setProducts([]);
      setTotalRecords(0);
    }
  };

  const reorderProductsLocally = (sourceIndex: number, targetIndex: number) => {
    setProducts((prevProducts) => {
      const updated = [...prevProducts];
      const [movedItem] = updated.splice(sourceIndex, 1);
      updated.splice(targetIndex, 0, movedItem);
      return updated.map((item, idx) => ({
        ...item,
        displayOrder: page * rowsPerPage + idx + 1,
      }));
    });
  };

  // ✅ Trigger fetch ONLY after debounce
  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted, page, rowsPerPage, debouncedSearch, categoryFilter]);

  return {
    products,
    totalRecords,
    loading,
    refetchProducts: fetchProducts,
    fetchProductsQuietly,
    reorderProductsLocally,
  };
};
